// Derived values for the Home dashboard. Pure functions, so they're testable without a DOM.

import type { Entry } from '../api/entries';

export function greetingFor(now: Date): string {
  const hour = now.getHours();
  if (hour < 5) return 'Still awake';
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  if (hour < 22) return 'Good evening';
  return 'Good night';
}

/**
 * The entries a count is allowed to speak for.
 *
 * A draft has not arrived yet: the journal's feeds and its category filters all hold drafts back, so
 * anything that counts drafts is describing a set the user cannot open. That is how the dashboard
 * came to say fourteen dreams while the Dreams filter showed five. Both sides read through here so
 * the two can only disagree by someone deliberately changing this rule.
 */
export function publishedEntries(entries: Entry[]): Entry[] {
  return entries.filter((e) => !e.isDraft);
}

/** Local-midnight day key, so "did I write today" follows the user's calendar, not UTC. */
function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

export interface RhythmDay {
  /** Local midnight of the day this cell stands for. */
  date: Date;
  entries: number;
}

export interface Rhythm {
  /** Oldest first, one per day, ending today. Length is a whole number of weeks minus the days
   *  remaining in the current one, so index % 7 is always the weekday. */
  days: RhythmDay[];
  /** Every entry ever written, not just the ones inside the window. */
  total: number;
  /** Days inside the window with at least one entry. */
  activeDays: number;
  lastWroteAt: Date | null;
}

/**
 * The shape of someone's writing over the last few months, as one cell per day.
 *
 * This replaces a consecutive-day streak, which was actively hostile: the number is zero for anyone
 * who missed a day, so the dashboard greeted a lapsed writer with a large 0 and the words "days
 * writing". A field of days carries the same information — including, honestly, the gaps — without
 * reducing a practice to whether it is currently unbroken.
 *
 * The window is extended back to a week boundary so that every column of seven is a real calendar
 * week and each row is a fixed weekday.
 */
export function writingRhythm(
  entries: Entry[],
  { weeks = 10, now = new Date() }: { weeks?: number; now?: Date } = {},
): Rhythm {
  const counts = new Map<string, number>();
  let lastWroteAt: Date | null = null;

  for (const entry of entries) {
    const key = dayKey(entry.createdAt);
    counts.set(key, (counts.get(key) ?? 0) + 1);
    if (!lastWroteAt || entry.createdAt > lastWroteAt) lastWroteAt = entry.createdAt;
  }

  const today = startOfDay(now);
  const first = new Date(today);
  first.setDate(first.getDate() - (weeks * 7 - 1));
  // Rewind to Monday so the grid's rows line up with weekdays.
  first.setDate(first.getDate() - ((first.getDay() + 6) % 7));

  const days: RhythmDay[] = [];
  for (const cursor = new Date(first); cursor <= today; cursor.setDate(cursor.getDate() + 1)) {
    days.push({ date: new Date(cursor), entries: counts.get(dayKey(cursor)) ?? 0 });
  }

  return {
    days,
    total: entries.length,
    activeDays: days.filter((d) => d.entries > 0).length,
    lastWroteAt,
  };
}

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export interface CountedSymbol {
  name: string;
  count: number;
}

/** Canonical and user-entered labels share an identity throughout the Symbol Vault. */
export function symbolKey(value: string): string {
  return value.trim().toLowerCase().replace(/^[^:]+:/, '').trim().replace(/\s+/g, ' ');
}

/** Published entries containing each symbol, most frequent first. */
export function countedSymbols(entries: Entry[]): CountedSymbol[] {
  const counts = new Map<string, CountedSymbol>();
  for (const entry of publishedEntries(entries)) {
    const seen = new Set<string>();
    for (const symbol of entry.symbols) {
      const key = symbolKey(symbol);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const existing = counts.get(key);
      if (existing) existing.count += 1;
      else counts.set(key, { name: symbol.trim().replace(/^[^:]+:/, '').trim().replace(/\s+/g, ' '), count: 1 });
    }
  }
  return [...counts.values()]
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

/** "just now", "2 hours ago", "3 days ago", then a date. */
export function relativeTime(date: Date, now: Date = new Date()): string {
  const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;

  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** "Tonight · Tue", "Yesterday · Mon", "Sun · Jun 1" — the Journal feed's day headers. */
export function dayHeading(date: Date, now: Date = new Date()): string {
  const weekday = date.toLocaleDateString(undefined, { weekday: 'short' });

  if (dayKey(date) === dayKey(now)) {
    return `${date.getHours() >= 18 || date.getHours() < 5 ? 'Tonight' : 'Today'} · ${weekday}`;
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dayKey(date) === dayKey(yesterday)) return `Yesterday · ${weekday}`;

  return `${weekday} · ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

/** Group entries into day buckets for the feed, newest day first. */
export function groupByDay(entries: Entry[], now: Date = new Date()): { day: string; items: Entry[] }[] {
  const groups = new Map<string, Entry[]>();
  for (const entry of entries) {
    const key = dayKey(entry.createdAt);
    const bucket = groups.get(key);
    if (bucket) bucket.push(entry);
    else groups.set(key, [entry]);
  }

  return [...groups.values()]
    .sort((a, b) => b[0].createdAt.getTime() - a[0].createdAt.getTime())
    .map((items) => ({ day: dayHeading(items[0].createdAt, now), items }));
}

/** A one-line preview for the feed, collapsing the whitespace a long entry will have. */
export function snippet(body: string, max = 140): string {
  const flat = body.replace(/\s+/g, ' ').trim();
  return flat.length <= max ? flat : `${flat.slice(0, max).trimEnd()}…`;
}

export function timeLabel(date: Date): string {
  return date
    .toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    .replace(/\s?([AP])M/i, (_, m: string) => m.toLowerCase());
}
